import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Bot, User, Sparkles, Loader2, MessageSquare, Trash2, Globe, TrendingUp, ShieldCheck, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { supabase } from '../supabase/config';
import { chatWithSuperAdminAI } from '../services/aiService';

const SuperAdminAISidebar = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { user, userData } = useAuth();
    const { showConfirm } = useAlert();
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Merhaba Sayın Admin! Ben Stratejik Büyüme Danışmanın. Bugün hangi işletmeleri platformumuza kazandıralım veya hangi teknik desteği planlayalım?' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!isOpen || !user?.uid) return;

        // Initial fetch
        const fetchChats = async () => {
            const { data } = await supabase.from('admin_ai_chats')
                .select('*')
                .eq('user_id', user.uid)
                .order('updated_at', { ascending: false })
                .limit(20);
            setChatHistory(data || []);
        };
        fetchChats();

        // Realtime subscription
        const channel = supabase.channel('admin_ai_chats_changes')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'admin_ai_chats',
                filter: `user_id=eq.${user.uid}`
            }, () => {
                fetchChats();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [isOpen, user?.uid]);

    const startNewChat = () => {
        setMessages([{ role: 'assistant', content: 'Merhaba Sayın Admin! Ben Stratejik Büyüme Danışmanın. Bugün hangi işletmeleri platformumuza kazandıralım veya hangi teknik desteği planlayalım?' }]);
        setActiveChatId(null);
        setShowHistory(false);
        setError(null);
    };

    const loadChat = (chat) => {
        setMessages(chat.messages);
        setActiveChatId(chat.id);
        setShowHistory(false);
        setError(null);
    };

    const deleteChat = async (e, id) => {
        e.stopPropagation();
        if (!await showConfirm('Bu sohbeti silmek istediğinize emin misiniz?', { title: 'Sohbeti Sil', confirmText: 'Sil', type: 'warning' })) return;
        try {
            await supabase.from('admin_ai_chats').delete().eq('id', id);
            setChatHistory(prev => prev.filter(c => c.id !== id));
            if (activeChatId === id) startNewChat();
        } catch (err) {
            console.error("Error deleting chat:", err);
        }
    };

    const getSuperContext = async () => {
        try {
            const [{ data: tenantsData }, { data: meetingsData }, { data: usersData }] = await Promise.all([
                supabase.from('tenants').select('*'),
                supabase.from('system_meetings').select('*').order('date', { ascending: false }).limit(15),
                supabase.from('users').select('id')
            ]);

            const tenantsArr = tenantsData || [];
            const meetings = (meetingsData || []).map(data => {
                const tenant = tenantsArr.find(t => t.id === data.tenant_id);
                return {
                    businessName: tenant?.name || data.custom_business_name,
                    date: data.date,
                    notes: data.notes
                };
            });

            return {
                tenants: tenantsArr.map(t => ({ name: t.name, industry: t.industry, id: t.id })),
                meetings: meetings,
                stats: {
                    totalBusinesses: tenantsArr.length,
                    totalUsers: (usersData || []).length
                }
            };
        } catch (err) {
            console.error("Super Admin AI context error:", err);
            return null;
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        setError(null);
        const userMessage = { role: 'user', content: input };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInput('');
        setLoading(true);

        try {
            const contextData = await getSuperContext();
            const response = await chatWithSuperAdminAI(input, messages, contextData);
            const finalMessages = [...updatedMessages, { role: 'assistant', content: response }];
            setMessages(finalMessages);

            if (user?.uid) {
                if (!activeChatId) {
                    const title = input.length > 40 ? input.substring(0, 40) + '...' : input;
                    const { data: newChat } = await supabase.from('admin_ai_chats').insert({
                        user_id: user.uid,
                        title: title,
                        messages: finalMessages,
                        updated_at: new Date().toISOString(),
                        created_at: new Date().toISOString()
                    }).select().single();
                    setActiveChatId(newChat.id);
                } else {
                    await supabase.from('admin_ai_chats').update({
                        messages: finalMessages,
                        updated_at: new Date().toISOString()
                    }).eq('id', activeChatId);
                }
            }
        } catch (err) {
            console.error("Super Admin AI Response Error:", err);
            setError(err.message || 'Bir hata oluştu.');
            setMessages(prev => [...prev, { role: 'assistant', content: `Üzgünüm, şu anda cevap veremiyorum. Hata: ${err.message}` }]);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="ai-sidebar-panel" style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: '450px',
            background: 'var(--bg-sidebar)', borderLeft: '1px solid var(--border)',
            boxShadow: '-20px 0 50px rgba(0,0,0,0.15)', zIndex: 2000,
            display: 'flex', flexDirection: 'column',
            animation: 'slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            color: 'var(--text-main)'
        }}>
            <style>{`
                @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
                @media (max-width: 600px) { .ai-sidebar-panel { width: 100% !important; border-left: none !important; } }
                .chat-messages::-webkit-scrollbar { width: 6px; }
                .chat-messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 10px; }
                .msg-bubble { max-width: 90%; padding: 16px 20px; border-radius: 20px; margin-bottom: 20px; font-size: 1rem; line-height: 1.6; position: relative; }
                .msg-user { align-self: flex-end; background: var(--primary); color: var(--text-on-primary); border-bottom-right-radius: 4px; box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.2); }
                .msg-assistant { align-self: flex-start; background: var(--bg-card); color: var(--text-main); border-bottom-left-radius: 4px; border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
                .status-badge { display: flex; align-items: center; gap: 6px; padding: 4px 10px; background: rgba(16, 185, 129, 0.1); color: #10b981; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
                .msg-assistant strong { color: var(--primary); font-weight: 800; }
                [data-theme='dark'] .msg-assistant strong { color: #d1b8a5; }
            `}</style>

            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(59, 130, 246, 0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{
                        background: 'var(--primary)',
                        borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-on-primary)',
                        boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.2)'
                    }}>
                        <ShieldCheck size={24} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 800 }}>Stratejik Büyüme Rehberi</h2>
                        <div className="status-badge">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></div>
                            Global Vision Modu
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        style={{ background: 'rgba(var(--primary-rgb), 0.05)', color: showHistory ? 'var(--primary)' : 'var(--text-main)', padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                        <MessageSquare size={20} />
                    </button>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', color: 'white', padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                        <X size={20} />
                    </button>
                </div>
            </div>

            {showHistory ? (
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', background: 'rgba(0,0,0,0.3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#94a3b8' }}>Geçmiş Sohbetler</h3>
                        <button onClick={startNewChat} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Plus size={14} /> Yeni Sohbet
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {chatHistory.length === 0 ? <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>Henüz geçmiş bulunmuyor.</div> : chatHistory.map(chat => (
                            <div key={chat.id} onClick={() => loadChat(chat)} style={{ padding: '12px 16px', borderRadius: '12px', background: activeChatId === chat.id ? 'rgba(59, 130, 246, 0.1)' : '#1e293b', border: activeChatId === chat.id ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{chat.title}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>{chat.updated_at ? new Date(chat.updated_at).toLocaleString() : ''}</div>
                                </div>
                                <button onClick={(e) => deleteChat(e, chat.id)} style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer' }}><Trash2 size={16} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    <div style={{ padding: '0.75rem 1.5rem', display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', overflowX: 'auto' }}>
                        {[{ icon: Globe, label: 'Pazar Araştırması' }, { icon: TrendingUp, label: 'Satış Stratejisi' }, { icon: Bot, label: 'Teknik Yol Haritası' }].map((act, i) => (
                            <button key={i} onClick={() => setInput(act.label)} style={{ whiteSpace: 'nowrap', padding: '6px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <act.icon size={14} /> {act.label}
                            </button>
                        ))}
                    </div>

                    <div className="chat-messages" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`msg-bubble msg-${msg.role}`} style={{ whiteSpace: 'pre-wrap' }}>
                                {msg.content.split(/(\*\*.*?\*\*|### .*?\n)/).map((part, i) => {
                                    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: msg.role === 'assistant' ? '#60a5fa' : 'inherit' }}>{part.slice(2, -2)}</strong>;
                                    if (part.startsWith('### ')) return <h3 key={i} style={{ fontSize: '1rem', fontWeight: 800, margin: '1rem 0 0.5rem 0', color: '#93c5fd' }}>{part.slice(4)}</h3>;
                                    return part;
                                })}
                            </div>
                        ))}
                        {loading && <div className="msg-bubble msg-assistant" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Loader2 size={18} className="animate-spin" /><span style={{ color: '#94a3b8' }}>Analiz ediliyor...</span></div>}
                        <div ref={messagesEndRef} />
                    </div>

                    <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                        <form onSubmit={handleSend} style={{ display: 'flex', gap: '12px' }}>
                            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Örn: X işletmesini nasıl kazanabiliriz?" style={{ flex: 1, padding: '14px 18px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '16px', fontSize: '1rem', color: 'var(--text-main)', outline: 'none' }} />
                            <button type="submit" disabled={loading || !input.trim()} style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px', background: loading || !input.trim() ? 'var(--border)' : 'var(--primary)', border: 'none', cursor: 'pointer' }}>
                                <Send size={22} color="var(--text-on-primary)" />
                            </button>
                        </form>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '12px' }}>
                            <Globe size={12} color="#60a5fa" /><p style={{ fontSize: '0.7rem', color: '#64748b' }}>Stratejik pazar ve işletme analizi aktif.</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SuperAdminAISidebar;
